import React from 'react';
import PropTypes from 'prop-types';
import Text from 'relements/build/esm/input/text';
import Checkbox from 'relements/build/esm/input/checkbox';

// External

import { generateNodeName } from '@src/utils/NodeUtils';
import {
  NODE_TYPES,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from '@src/constants/MogamboConstants';
import UserResponseSynonymTable from '@src/components/UserResponseSynonymTable';
import BotSaysCard from '@src/components/BotResponses/BotSays/BotSaysCard';
import { isNewPipeline as checkIsNewPipeline } from '@src/actions/_utils';
import BotStore from '@src/store';

import './GeneralNodeItem.scss';

export default class GeneralNodeItem extends React.Component {
  render() {
    const transposedUserResponses = this.props.user_responses.map(
      (userResponse) => ({
        id: userResponse.id,
        active: userResponse.active,
        title: userResponse.sentence,
        tags: userResponse.keywords,
        is_system_generated: userResponse.is_system_generated,
        is_disambiguation_suggestion: userResponse.is_disambiguation_suggestion,
      })
    );

    return (
      <div className="general-nodes-overlay-item-body">
        {this.renderHeader()}
        <div className="general-nodes-overlay-item-responses">
          {this.renderUserSays(transposedUserResponses)}
          {this.renderBotSays()}
        </div>
      </div>
    );
  }

  renderHeader = () => {
    const nodeContextMenuEnabled = BotStore.getEnableNodeContextMenuFlag();
    return (
      <header className="general-nodes-overlay-item-body-header">
        <Text
          className="general-nodes-overlay-item-body-header-input"
          id={`${this.props.id}|title`}
          label="Step Name"
          type="text"
          value={this.props.title}
          updateValue={this._onUpdate}
          onChange={(e) =>
            this._onUpdate({
              id: `${this.props.id}|title|`,
              value: e,
            })}
        />

        <Checkbox.Item
          label={`Mark as ${
            nodeContextMenuEnabled ? 'End of Query' : 'Context Clear'
          } Step?`}
          className="general-nodes-overlay-item-body-header-checkbox"
          value={
            nodeContextMenuEnabled
              ? this.props[END_OF_QUERY.KEY]
              : this.props[CONTEXT_CLEAR_NODE.KEY]
          }
          onChange={(e) =>
            this._onUpdate({
              id: `${this.props.id}|${
                nodeContextMenuEnabled
                  ? END_OF_QUERY.KEY
                  : CONTEXT_CLEAR_NODE.KEY
              }|`,
              value: e,
            })}
        />
      </header>
    );
  };

  renderUserSays = (transposedUserResponses) => (
    <div className="general-nodes-overlay-item-user-says">
      <UserResponseSynonymTable
        context={{
          connectingNode: 'default',
          nodeId: this.props.node_id,
          domainId: this.props.domain.id,
          domainTitle: this.props.domain.title,
          isNewPipeline: checkIsNewPipeline(this.props.domain),
        }}
        screen="default"
        innerdata={{ default: transposedUserResponses }}
        editedData={{}}
        contextMenuOffset={{ y: 160, x: -30 }}
        synonyms={this.props.synonyms}
        updatedSynonyms={this.props.updatedSynonyms}
        meanings={this.props.meanings}
        noAutoFocus
        updateInputs={(e) => {
          this._onUpdate({
            id: `${this.props.id}|user_responses|`,
            ...e,
          });
        }}
        style={{ paddingTop: 20 }}
        onFAQNode
      />
    </div>
  );

  renderBotSays = () => (
    <div className="general-nodes-overlay-item-bot-says">
      <div className="general-nodes-overlay-item-bot-says-title">
        Bot Responses:
      </div>
      <BotSaysCard
        title="Final Bot Reply"
        type="DEFAULT"
        value={{
          variants: this.props.bot_responses,
          quickReplies: this.props.quick_replies || [],
        }}
        hideHeader
        onChange={this._handleChange}
      />
    </div>
  );

  _handleChange = (lowerKey, value) => {
    lowerKey = lowerKey === 'variants' ? 'bot_responses' : 'quick_replies';
    this.props.dataUpdateCallback(this.props.node_id, lowerKey, value);
  };

  _onUpdate = (data) => {
    const splitData = data.id.split('|');
    switch (splitData[1]) {
      case 'title':
        this.props.dataUpdateCallback(this.props.node_id, 'title', data.value);
        if (this.props.new_node) {
          const domainName = this.props.domain.title;
          const storyName = this.props.story ? this.props.story.title : null;
          let systemName;
          if (storyName) {
            // Story General Node
            systemName = generateNodeName(
              NODE_TYPES.STORY_GENERAL,
              data.value,
              domainName,
              storyName
            );
          } else {
            // Bot General Node
            systemName = generateNodeName(
              NODE_TYPES.BOT_GENERAL,
              data.value,
              domainName
            );
          }
          this.props.dataUpdateCallback(
            this.props.node_id,
            'system_name',
            systemName
          );
        }
        break;

      case 'entity':
        this.props.dataUpdateCallback(this.props.node_id, 'entity', data.value);
        break;

      case 'user_responses': {
        const updatedResponses = data.default.map((userResponse) => ({
          id: userResponse.id,
          active: userResponse.active,
          sentence: userResponse.title,
          keywords: userResponse.tags,
          is_system_generated: userResponse.is_system_generated,
          is_disambiguation_suggestion:
            userResponse.is_disambiguation_suggestion,
        }));
        this.props.dataUpdateCallback(
          this.props.node_id,
          'user_responses',
          updatedResponses
        );
        break;
      }

      case 'bot_responses':
        this.props.dataUpdateCallback(
          this.props.node_id,
          'bot_responses',
          data.botResponses
        );
        break;

      case CONTEXT_CLEAR_NODE.KEY:
        this.props.dataUpdateCallback(
          this.props.node_id,
          CONTEXT_CLEAR_NODE.KEY,
          data.value
        );
        break;

      case END_OF_QUERY.KEY:
        this.props.dataUpdateCallback(
          this.props.node_id,
          END_OF_QUERY.KEY,
          data.value
        );
        break;

      default:
        break;
    }
  };
}

GeneralNodeItem.propTypes = {
  active: PropTypes.bool,
  quick_replies: PropTypes.arrayOf(PropTypes.string),
  synonyms: PropTypes.object,
  updatedSynonyms: PropTypes.object,
  meanings: PropTypes.object,
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  story: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  context_clear: PropTypes.bool,
  system_name: PropTypes.string,
  title: PropTypes.string,
  user_responses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      active: PropTypes.bool,
      title: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
      is_system_generated: PropTypes.bool,
    })
  ),
  dataUpdateCallback: PropTypes.func,
  bot_responses: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  node_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  new_node: PropTypes.bool,
};
